import React, { Component } from 'react';
import styled from 'styled-components';
import { Chip } from './DashboardPanel';
import MultiLineChart from './MultiLineChart';
import { fmtValue, validateHeatcast, getCblAverage } from '../model/dashboardCalc';
import {
  HEATCAST_EXAMPLE,
  HEATCAST_MEASURED_RESULT,
  HEATCAST_MEASURED_MESSAGE,
  HEATCAST_INPUT_MESSAGE,
  HEATCAST_SERIES_COLOR,
  HEATCAST_STORAGE_KEY,
} from '../model/heatcastResult';

// HEATCAST 예측 탭: AI 학습 · 수요예측 · 감축 시뮬레이션 결과
class ForecastSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      result: HEATCAST_MEASURED_RESULT,
      message: HEATCAST_MEASURED_MESSAGE,
      editorOpen: false,
      json: '',
      error: '',
    };
    this.editorRef = React.createRef();
  }

  openEditor = (json, error = '') => {
    this.setState(
      (prev) => ({ editorOpen: true, json: json === undefined ? prev.json : json, error }),
      () => this.editorRef.current && this.editorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    );
  };

  handleShowExample = () => {
    this.openEditor(JSON.stringify(HEATCAST_EXAMPLE, null, 2), '형식 확인용 예시입니다. 실제 결과가 자동 적용되지는 않습니다.');
  };

  handleApply = () => {
    try {
      const result = validateHeatcast(JSON.parse(this.state.json));
      this.setState((prev) => ({
        result: {
          ...result,
          series: result.series || prev.result.series,
          dr: result.dr || prev.result.dr,
        },
        message: {
          source: HEATCAST_INPUT_MESSAGE.source,
          curve: result.series ? HEATCAST_INPUT_MESSAGE.curve : prev.message.curve,
          dr: result.dr ? HEATCAST_INPUT_MESSAGE.dr : prev.message.dr,
        },
        editorOpen: false,
        error: '',
      }));
      try {
        localStorage.setItem(HEATCAST_STORAGE_KEY, this.state.json);
      } catch (e) {
        console.log(e);
      }
    } catch (e) {
      this.setState({ error: e.message });
    }
  };

  render() {
    const { result: o, message, editorOpen, json, error } = this.state;
    const engines = o.engines || [];
    const compliance = Math.max(0, Math.min(100, o.compliance_pct || 0));

    const kpis = [
      { label: '예측 정확도 · nMAE', value: fmtValue(o.nmae, '%'), meta: '검증 데이터 기준' },
      { label: '예측 정확도 · R²', value: fmtValue(o.r2, '', 3), meta: '홀드아웃 검증' },
      { label: '예측 CBL · 평균', value: fmtValue(getCblAverage(o), ' kW'), meta: 'DR 기준 부하 · 가상 시뮬레이션', highlight: true },
      { label: '감축 가능전력', value: fmtValue(o.avg_cut_kw, ' kW'), meta: '예시 평균 감축 · 가상', highlight: true },
      { label: '안전마진 반영 입찰량', value: fmtValue(o.bid_kw, ' kW'), meta: '예시 참여 규모 · 가상' },
      { label: '감축 지속시간', value: fmtValue(o.duration_h, ' h'), meta: '열버퍼 가상 추정' },
      { label: '시험 B 이행률', value: fmtValue(o.compliance_pct, '%'), meta: '시뮬레이션 예시 이행률' },
    ];

    return (
      <Wrap>
        <div className="hc-heading">
          <div>
            <div className="hc-eyebrow">AI DEMAND INTELLIGENCE / HEATCAST</div>
            <h2>AI 학습 · 수요예측 · 감축 시뮬레이션 결과</h2>
            <p>AI 학습 지표는 S6 데이터 분석값 · DR/열버퍼/시험 B 수치는 시연용 가상 예시</p>
          </div>
          <div className="hc-right">
            <Chip>{message.source}</Chip>
            <button type="button" className="hc-action" onClick={() => this.openEditor()}>
              결과 JSON 입력
            </button>
          </div>
        </div>

        <div className="hc-kpis">
          {kpis.map((kpi) => (
            <article key={kpi.label} className="hc-kpi">
              <span>{kpi.label}</span>
              <strong className={kpi.highlight ? 'highlight' : ''}>{kpi.value}</strong>
              <small>{kpi.meta}</small>
            </article>
          ))}
        </div>

        <div className="hc-two">
          <article className="hc-panel">
            <div className="hc-panel-head">
              <div>
                <h3>AI 학습 성능 분석</h3>
                <p>동일한 검증구간에서 엔진별 예측 정확도 비교</p>
              </div>
              <Chip>{o.engine || '학습 엔진 —'}</Chip>
            </div>
            <div className="hc-metrics">
              <div>
                <span>nRMSE</span>
                <b>{fmtValue(o.nrmse, '%')}</b>
              </div>
              <div>
                <span>RMSE</span>
                <b>{fmtValue(o.rmse_kw, ' kW')}</b>
              </div>
              <div>
                <span>학습 / 검증 분할</span>
                <b>{o.split || '75 : 25'}</b>
              </div>
            </div>
            <div className="hc-table-wrap">
              <table className="hc-table">
                <thead>
                  <tr>
                    <th>AI 엔진</th>
                    <th>nMAE</th>
                    <th>nRMSE</th>
                    <th>R²</th>
                  </tr>
                </thead>
                <tbody>
                  {engines.length === 0 ? (
                    <tr>
                      <td colSpan="4">엔진별 결과가 입력되지 않았습니다.</td>
                    </tr>
                  ) : (
                    engines.map((e, i) => (
                      <tr key={`${e.name}-${i}`}>
                        <td>{e.name || '—'}</td>
                        <td>{fmtValue(e.nmae, '%')}</td>
                        <td>{fmtValue(e.nrmse, '%')}</td>
                        <td>{fmtValue(e.r2, '', 3)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </article>

          <article className="hc-panel">
            <div className="hc-panel-head">
              <div>
                <h3>수요자원 감축 시뮬레이션</h3>
                <p>CBL · 감축 가능전력 · 입찰 안전마진 · 시험 B 예시</p>
              </div>
              <Chip>CBL · {o.cbl || '—'}</Chip>
            </div>
            <div className="hc-meter">
              <div className="hc-meter-head">
                <span>감축 이행률</span>
                <strong>{fmtValue(o.compliance_pct, '%')}</strong>
              </div>
              <div className="hc-meter-bg">
                <div style={{ width: `${compliance}%` }} />
              </div>
              <small>시험 B 이행률 예시 · 가상 값</small>
            </div>
            <div className="hc-metrics hc-metrics-2">
              <div>
                <span>입찰 안전마진</span>
                <b>{fmtValue(o.margin_pct, '%')}</b>
              </div>
              <div>
                <span>미이행 확률</span>
                <b>{fmtValue(o.fail_pct, '%')}</b>
              </div>
              <div>
                <span>리바운드 에너지</span>
                <b>{fmtValue(o.rebound_kwh, ' kWh')}</b>
              </div>
              <div>
                <span>축열 가용량</span>
                <b>{fmtValue(o.buffer_kwh, ' kWh')}</b>
              </div>
            </div>
          </article>
        </div>

        <div className="hc-two">
          <article className="hc-panel">
            <div className="hc-panel-head">
              <div>
                <h3>실측 부하 vs AI 예측 부하</h3>
                <p>첨부 S6 P2H 데이터 기반 시간순 홀드아웃 예측</p>
              </div>
              <span className="hc-legend">
                <i /> 실측 <i className="pred" /> AI 예측
              </span>
            </div>
            {o.series && (
              <MultiLineChart
                labels={o.series.labels}
                series={[
                  { data: o.series.actual, color: HEATCAST_SERIES_COLOR.ACTUAL },
                  { data: o.series.predicted, color: HEATCAST_SERIES_COLOR.PREDICTED },
                ]}
                ariaLabel="실측과 AI 예측 부하 비교 차트"
              />
            )}
            <div className="hc-muted">{message.curve}</div>
          </article>
          <article className="hc-panel">
            <div className="hc-panel-head">
              <div>
                <h3>감축 운전 · 열버퍼 분석</h3>
                <p>DR 감축 및 열버퍼 시뮬레이션 예시 · 가상 결과</p>
              </div>
              <Chip>시뮬레이션</Chip>
            </div>
            {o.dr && (
              <MultiLineChart
                labels={o.dr.labels}
                series={[
                  { data: o.dr.baseline, color: HEATCAST_SERIES_COLOR.ACTUAL },
                  { data: o.dr.controlled, color: HEATCAST_SERIES_COLOR.PREDICTED },
                ]}
                ariaLabel="감축 시뮬레이션 전력 그래프"
              />
            )}
            <div className="hc-muted">{message.dr}</div>
          </article>
        </div>

        <div className="hc-bottom">
          <div>
            <strong>결과 데이터 입력</strong>
            <p>
              첨부 S6 P2H 시험데이터(2,880시간)를 이용해 HDD 2차 회귀와 Random Forest를 학습하고, 마지막 25% 기간의
              홀드아웃 결과를 기본 표시합니다. DR 감축·열버퍼·CBL 수치는 화면 시연을 위한 가상 예시이며 실제 분석
              결과가 아닙니다. 별도 HEATCAST 결과 JSON을 적용할 수도 있습니다.
            </p>
          </div>
          <button type="button" className="hc-action hc-secondary" onClick={this.handleShowExample}>
            입력 형식 보기
          </button>
        </div>

        {editorOpen && (
          <div className="hc-editor" ref={this.editorRef}>
            <div className="hc-editor-head">
              <strong>HEATCAST 결과 JSON</strong>
              <button type="button" onClick={() => this.setState({ editorOpen: false })}>
                닫기 ×
              </button>
            </div>
            <textarea
              value={json}
              onChange={(e) => this.setState({ json: e.target.value })}
              placeholder="HEATCAST 분석 결과를 JSON 형식으로 붙여 넣으세요."
              spellCheck="false"
            />
            <div className="hc-editor-actions">
              <span>{error}</span>
              <button type="button" className="hc-action" onClick={this.handleApply}>
                결과 적용
              </button>
            </div>
          </div>
        )}
      </Wrap>
    );
  }
}

const Wrap = styled.div`
  .hc-heading {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin: 10px 0 22px;

    > * {
      min-width: 0;
    }
    h2 {
      font-size: 25px;
      font-weight: 700;
      margin: 6px 0;
      color: #edf3fc;
    }
  }
  .hc-heading p,
  .hc-panel-head p,
  .hc-bottom p {
    color: #91a6bf;
    font-size: 12px;
    margin: 4px 0;
    line-height: 1.65;
  }
  .hc-eyebrow {
    color: #ffd342;
    letter-spacing: 2px;
    font-size: 10px;
    font-weight: 800;
  }
  .hc-right {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    min-width: 0;
    max-width: 100%;
  }
  .hc-action {
    background: linear-gradient(120deg, #e5b633, #ffd95b);
    border: 0;
    color: #101b2a;
    font-weight: 800;
    border-radius: 9px;
    padding: 10px 15px;
    cursor: pointer;
  }
  .hc-secondary {
    background: #192c45;
    color: #dceaff;
    border: 1px solid #36516e;
  }
  .hc-kpis {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 11px;
    margin-bottom: 15px;
  }
  .hc-kpi,
  .hc-panel,
  .hc-bottom,
  .hc-editor {
    background: linear-gradient(155deg, #15243a, #0e1a2e);
    border: 1px solid #293c55;
    border-radius: 16px;
    box-shadow: 0 10px 28px #0002;
  }
  .hc-kpi {
    padding: 18px 15px;
    min-width: 0;

    span,
    small {
      display: block;
      color: #91a7bf;
      font-size: 11px;
    }
    strong {
      display: block;
      font-size: 27px;
      color: #f3f7ff;
      letter-spacing: -1px;
      margin: 11px 0;

      &.highlight {
        color: #ffcf54;
      }
    }
  }
  .hc-two {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-bottom: 15px;
  }
  .hc-panel {
    padding: 20px;
    min-width: 0;
  }
  .hc-panel-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 18px;

    h3 {
      font-size: 16px;
      font-weight: 700;
      margin: 0;
      color: #edf3fc;
    }
  }
  .hc-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 16px;

    > div {
      background: #0a192d;
      border: 1px solid #243952;
      border-radius: 11px;
      padding: 13px;
    }
    span {
      display: block;
      color: #91a6c1;
      font-size: 11px;
      margin-bottom: 8px;
    }
    b {
      font-size: 17px;
      color: #edf3fc;
    }
  }
  .hc-metrics-2 {
    grid-template-columns: repeat(2, 1fr);
  }
  .hc-table-wrap {
    overflow: auto;
  }
  .hc-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th {
      text-align: left;
      color: #91a8c4;
      background: #14283f;
      padding: 11px 9px;
    }
    td {
      padding: 12px 9px;
      border-bottom: 1px solid #253951;
      color: #e8f2ff;
    }
  }
  .hc-meter {
    padding: 16px;
    background: #0b1b2e;
    border-radius: 12px;
    margin-bottom: 16px;

    .hc-meter-head {
      display: flex;
      justify-content: space-between;
      color: #e8f2ff;
    }
    strong {
      font-size: 23px;
      color: #ffcf54;
    }
    small {
      color: #8ca1bb;
      font-size: 11px;
    }
  }
  .hc-meter-bg {
    height: 9px;
    border-radius: 8px;
    background: #233a52;
    margin: 13px 0;
    overflow: hidden;

    > div {
      height: 100%;
      background: linear-gradient(90deg, #45bfe5, #6de0a2);
      transition: width 0.4s;
    }
  }
  .hc-muted {
    color: #8ca1bb;
    font-size: 11px;
  }
  .hc-legend {
    color: #a5bad2;
    font-size: 11px;
    white-space: nowrap;

    i {
      display: inline-block;
      width: 12px;
      height: 3px;
      background: #43b9ed;
      vertical-align: middle;
      margin: 0 5px;

      &.pred {
        background: #f6c858;
      }
    }
  }
  .hc-bottom {
    padding: 18px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    color: #e8f2ff;
  }
  .hc-editor {
    padding: 20px;
    margin-top: 12px;
    color: #e8f2ff;
  }
  .hc-editor-head,
  .hc-editor-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  .hc-editor-head button {
    background: none;
    border: 0;
    color: #aec5df;
    cursor: pointer;
  }
  .hc-editor textarea {
    margin: 14px 0;
    width: 100%;
    height: 220px;
    resize: vertical;
    background: #081628;
    color: #eaf2ff;
    border: 1px solid #35506c;
    border-radius: 10px;
    padding: 15px;
    font-family: monospace;
    font-size: 12px;
  }
  .hc-editor-actions span {
    color: #ff9d99;
    font-size: 12px;
  }

  @media (max-width: 1250px) {
    .hc-kpis {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
  @media (max-width: 760px) {
    .hc-two {
      grid-template-columns: minmax(0, 1fr);
    }
    .hc-heading,
    .hc-bottom {
      flex-direction: column;
      align-items: stretch;
    }
    .hc-heading h2 {
      font-size: 20px;
    }
    .hc-right {
      justify-content: flex-start;
    }
  }
  @media (max-width: 700px) {
    .hc-kpis {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }
    .hc-kpi {
      padding: 14px 11px;
      strong {
        font-size: 22px;
        overflow-wrap: anywhere;
      }
    }
    .hc-panel {
      padding: 15px 12px;
    }
    .hc-panel-head {
      flex-wrap: wrap;
    }
    .hc-metrics,
    .hc-metrics-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }
    .hc-metrics > div {
      padding: 11px;
    }
    .hc-metrics b {
      font-size: 15px;
    }
    .hc-table {
      min-width: 400px;
    }
    .hc-editor {
      padding: 12px;
    }
    .hc-editor textarea {
      font-size: 12px;
    }
    .hc-editor-actions {
      flex-wrap: wrap;
    }
  }
`;

export default ForecastSection;
